import { useEffect, useState } from 'react';
import type { User } from '@/types/api';
import { Button, StatusBadge } from '@/components/common';
import { getWorkspaceStatus, disconnectWorkspace } from '@/api/google-workspace';
import { get } from '@/api/client';

interface AccountSettingsProps {
  user: User;
}

export function AccountSettings({ user }: AccountSettingsProps) {
  const [workspaceConnected, setWorkspaceConnected] = useState(false);
  const [workspaceScopes, setWorkspaceScopes] = useState<string[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    getWorkspaceStatus()
      .then((status) => {
        setWorkspaceConnected(status.connected);
        setWorkspaceScopes(status.scopes);
      })
      .catch(() => {})
      .finally(() => setWorkspaceLoading(false));
  }, []);

  const handleConnect = async () => {
    try {
      const data = await get<{ url: string }>('/api/user/google-workspace/connect', {
        redirect_to: window.location.origin,
      });
      window.location.href = data.url;
    } catch {
      // Auth error handled by global interceptor
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectWorkspace();
      setWorkspaceConnected(false);
      setWorkspaceScopes([]);
    } catch {
      // silent
    } finally {
      setDisconnecting(false);
    }
  };

  const hasCalendar = workspaceScopes.some((s) => s.includes('calendar'));
  const hasGmail = workspaceScopes.some((s) => s.includes('gmail'));

  return (
    <div id="accountSettingsPanel" className="settings-tab-panel">
      {/* Profile */}
      <div className="settings-section">
        <h3 className="section-title">Profile</h3>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            id="userEmail"
            className="form-input"
            value={user.email}
            readOnly
          />
        </div>
        <div className="form-group">
          <label className="form-label">Name</label>
          <input
            type="text"
            id="userNameSetting"
            className="form-input"
            placeholder="Enter your name"
            defaultValue={user.name || ''}
          />
        </div>
      </div>

      {/* Google Workspace */}
      <div className="settings-section">
        <div className="section-header">
          <h3 className="section-header__title">Google Workspace</h3>
          <p className="section-header__description">
            Connect your Google account to pull calendar events and emails into your daily
            planning view.
          </p>
        </div>

        {workspaceLoading ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Checking connection...</p>
        ) : workspaceConnected ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-success, #22c55e)',
                  display: 'inline-block',
                }}
              />
              <span style={{ fontSize: '14px', fontWeight: 500 }}>Connected</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {hasCalendar && (
                <StatusBadge label="Calendar" tone="success" size="sm" />
              )}
              {hasGmail && (
                <StatusBadge label="Gmail" tone="success" size="sm" />
              )}
            </div>
            <Button
              variant="secondary"
              onClick={handleDisconnect}
              isLoading={disconnecting}
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <Button onClick={handleConnect}>Connect Google Workspace</Button>
        )}
      </div>
    </div>
  );
}

export default AccountSettings;
